import { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Card } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { Alert, BackButton, DataStatus, ActionButton } from "components";
import { DateConvert } from "utilities";
import { FakturPenjualanApi } from "api";
import { InfoSection, FormSection, TableSection, TableHistory } from "../Section";
import axios from "axios";

const TambahFakturPenjualan = ({ setNavbarTitle }) => {
  const today = DateConvert(new Date()).default;
  const { id } = useParams();
  const history = useHistory();
  const [dataInfo, setDataInfo] = useState({});
  const [dataTable, setDataTable] = useState([]);
  const [dataPrelim, setDataPrelim] = useState([]);
  const [dataHistory, setDataHistory] = useState([]);
  const [isPrelim, setIsPrelim] = useState(false);
  const [formRendered, setFormRendered] = useState(0);
  const [fetchingStatus, setFetchingStatus] = useState({
    loading: false,
    success: true,
  });
  const [alertConfig, setAlertConfig] = useState({
    show: false,
    variant: "primary",
    text: "",
  });

  const mappingDataInfo = (data) => {
    return {
      id_sales_order: data.id_sales_order ?? "-",
      tgl_sales_order: data.tgl_sales_order ?? null,
      no_sales_order: data.no_sales_order ?? "-",
      customer: data.nama_customer ?? "-",
      sales: data.nama_sales ?? "-",
      nama_proyek: data.nama_proyek ?? "-",
      batas_waktu: data.batas_waktu ?? null,
      ppn: parseFloat(data.ppn ?? 0),
      diskon: parseFloat(data.diskon ?? 0),
      uang_muka: parseFloat(data.uang_muka ?? 0),
      akumulasi_prelim: data.akumulasi_prelim ?? 0,
    };
  };

  const mappingDataPrelim = (data) => {
    const prelim = data?.map((item, index) =>
      Object({
        ...item,
        id_item: item.id_item_prelim,
        kode_item: item.kode_item_prelim,
        nama_item: item.nama_item_prelim,
        qty_item: item.qty_analisa,
        id_unit: item.id_satuan_prelim,
        nama_unit: item.nama_satuan_prelim,
        harga: parseFloat(item.unit_price ?? 0),
        qty_durasi: parseFloat(item.qty_durasi ?? 0),
        konstanta: parseFloat(item.konstanta ?? 0),
        order: index + 1,
      })
    );

    return prelim;
  };

  const mappingDataTable = (data) =>
    data.map((val) => {
      return {
        id_surat_jalan_detail: val.id_surat_jalan_detail ?? null,
        id_surat_jalan: val.id_surat_jalan ?? null,
        tgl_surat_jalan: val.tgl_surat_jalan ?? null,
        no_surat_jalan: val.no_surat_jalan ?? "-",
        tgl_penerimaan_surat_jalan: val.tgl_penerimaan_surat_jalan ?? null,
        no_penerimaan_surat_jalan: val.no_penerimaan_surat_jalan ?? "-",
        no_delivery_order: val.no_delivery_order ?? "-",
        tgl_delivery_order: val.tgl_delivery_order ?? null,
        nama_item: val.nama_barang ?? "-",
        satuan: val.nama_satuan ?? "",
        qty: val.qty ? parseFloat(val.qty) : 0,
        harga: val.harga_satuan_jual ? parseInt(val.harga_satuan_jual) : 0,
        checked: false,
        instalasi: val.instalasi,
        persentase_progress: !val.instalasi ? 100 : 0,
        akumulasi_progress: parseInt(val.akumulasi_progress ?? 0),
      };
    });

  const getInitialData = () => {
    setFetchingStatus({
      loading: true,
      success: false,
    });

    axios
      .all([
        FakturPenjualanApi.getSingleSO({ id_sales_order: id }),
        FakturPenjualanApi.getHistoryFaktur({ id_sales_order: id }),
      ])
      .then(
        axios.spread((resSingle, resHistory) => {
          const getData = resSingle?.data?.data;
          const getHistory = resHistory?.data?.data ?? [];

          const checkPrelim = getData.prelim ? true : false;
          const mapDataInfo = mappingDataInfo(getData ?? {});
          const mapDataTable = mappingDataTable(getData.surat_jalan ?? []);
          const mapDataPrelim = mappingDataPrelim(getData.prelim ?? []);

          setIsPrelim(checkPrelim);
          setDataInfo(mapDataInfo);
          setDataTable(mapDataTable);
          setDataPrelim(mapDataPrelim);
          setDataHistory(getHistory);
          setFetchingStatus({
            loading: false,
            success: true,
          });
        })
      )
      .catch(() => {
        setAlertConfig({ show: false, variant: "primary", text: "Data gagal dimuat!" });
        setFetchingStatus({ loading: false, success: false });
      });
  };

  const formInitialValues = {
    id_sales_order: dataInfo.id_sales_order,
    tanggal: today,
    nomor: "",
    diskon: dataInfo.diskon ?? 0,
    ppn: dataInfo.ppn ?? 0,
    persentase_prelim: 0,
    uang_muka: dataInfo.uang_muka ?? 0,
    akumulasi_prelim: dataInfo.akumulasi_prelim ?? 0,
  };

  const formValidationSchema = Yup.object().shape({
    tanggal: Yup.string().required("Pilih tanggal"),
    nomor: Yup.string().required("Pilih tanggal untuk menentukan nomor"),
  });

  const formSubmitHandler = (values, { setSubmitting }) => {
    const filterDataTable = dataTable.filter((val) => val.checked === true);
    const getDataTable = filterDataTable.map((val) =>
      Object({
        id_surat_jalan_detail: val.id_surat_jalan_detail,
        persentase_progress: val.persentase_progress,
      })
    );

    const finalValues = {
      id_sales_order: values.id_sales_order,
      tgl_faktur: values.tanggal,
      persentase_prelim: values.persentase_prelim,
      ppn: values.ppn,
      diskon: values.diskon,
      detail: getDataTable,
    };

    FakturPenjualanApi.save(finalValues)
      .then(() =>
        history.push("/sls/transaksi/faktur-penjualan", {
          alert: {
            show: true,
            variant: "primary",
            text: "Data berhasil disimpan!",
          },
        })
      )
      .catch(() => {
        setAlertConfig({
          show: true,
          variant: "danger",
          text: "Data gagal disimpan!",
        });
      })
      .finally(() => setSubmitting(false));
  };

  useEffect(() => {
    getInitialData();
    setNavbarTitle("Faktur Penjualan");
  }, []);

  return (
    <Card style={{ minHeight: "80vh" }}>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <b>Tambah Data Faktur Penjualan</b>
        <BackButton size="sm" onClick={() => history.goBack()} />
      </Card.Header>
      <Card.Body className="pb-5">
        <Alert
          show={alertConfig.show}
          variant={alertConfig.variant}
          text={alertConfig.text}
          showCloseButton={true}
          onClose={() => setAlertConfig({ ...alertConfig, show: false })}
        />
        {fetchingStatus.loading ? (
          <DataStatus loading text="Memuat . . ." />
        ) : fetchingStatus.success ? (
          <Formik
            initialValues={formInitialValues}
            validationSchema={formValidationSchema}
            onSubmit={formSubmitHandler}
          >
            {(formik) => (
              <form onSubmit={formik.handleSubmit}>
                <InfoSection dataInfo={dataInfo} />
                <hr className="mb-1" />
                <FormSection
                  formik={formik}
                  formRendered={formRendered}
                  fetchingStatus={fetchingStatus}
                  setFormRendered={setFormRendered}
                />
                <hr className="mt-1" />
                <TableSection
                  formik={formik}
                  dataTable={dataTable}
                  setDataTable={setDataTable}
                  dataPrelim={dataPrelim}
                  setDataPrelim={setDataPrelim}
                  dataHistory={dataHistory}
                  isPrelim={isPrelim}
                />
                <hr />
                <TableHistory dataHistory={dataHistory} />
                <div className="d-flex justify-content-end">
                  <ActionButton
                    type="submit"
                    text="Simpan Faktur Penjualan"
                    loading={formik.isSubmitting}
                  />
                </div>
              </form>
            )}
          </Formik>
        ) : (
          <DataStatus text="Data gagal dimuat" />
        )}
      </Card.Body>
    </Card>
  );
};

export default TambahFakturPenjualan;
